/* eslint-disable no-mixed-spaces-and-tabs */
import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { getFormValues, isInvalid } from 'redux-form';
import { initialState } from './reducer';
import { ADD_CAR_FORM_NAME, ADD_TRAILER_FORM_NAME, DATE_TIME_FORMAT, FORM_NAME } from './constants';

export const carChangeModalDomain = state => state.carChangeModalReducer || initialState;
export const selectRouterDomain = state => state.router || {};

export const getFormInitialValuesSelector = () =>
	createSelector(carChangeModalDomain, ({ requestInfo }) => {
		if (!requestInfo) {
			return {
				driver_id: 'none',
				car_id: 'none',
				trailer_id: 'none',
			};
		}

		return {
			driver_id: requestInfo?.driver?.id ? requestInfo?.driver?.id : 'none',
			car_id: requestInfo?.car?.id ? requestInfo?.car?.id : 'none',
			trailer_id: requestInfo?.trailer?.id ? requestInfo?.trailer?.id : 'none',
		};
	});

export const getIsRequestInfoLoadingSelector = () =>
	createSelector(carChangeModalDomain, ({ isRequestInfoLoading }) => isRequestInfoLoading);

export const getIsChangeCarSelector = () =>
	createSelector(carChangeModalDomain, ({ requestInfo }) => {
		return !!requestInfo?.car?.id;
	});

export const getIsAddCarModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'add-car-modal');
	});

export const getIsAddTrailerModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(
			!isEmpty(query) && (query?.modal === 'add-trailer-modal' || query?.modal === 'add-trailer-to-main-modal'),
		);
	});

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(
			!isEmpty(query) &&
				['car_change', 'add-trailer-modal', 'add-car-modal', 'add-trailer-to-main-modal'].includes(
					query?.modal,
				),
		);
	});

export const getIsAllowedTrailerSelector = () =>
	createSelector(carChangeModalDomain, getFormValues(FORM_NAME), ({ cars }, formValues) => {
		return Boolean(cars.find(car => car?.value === formValues?.car_id && !car.disabled)?.isAllowedTrailer);
	});

export const getDataForSaveSelector = () =>
	createSelector(getFormValues(FORM_NAME), getIsAllowedTrailerSelector(), (formValues, isAllowedTrailer) => {
		const trailerId = formValues.trailer_id === 'none' ? null : formValues.trailer_id;

		return {
			driver_id: formValues.driver_id,
			car_id: formValues.car_id,
			...(isAllowedTrailer ? { trailer_id: trailerId } : {}),
		};
	});

export const getReqNameSelector = () =>
	createSelector(carChangeModalDomain, ({ requestInfo }) => {
		if (!requestInfo) return '';
		return `Заявка #${requestInfo?.id || '-'} от ${
			requestInfo?.created_at ? moment(requestInfo.created_at).format(DATE_TIME_FORMAT) : '-'
		}`;
	});

export const getModalStatusesSelector = () =>
	createSelector(carChangeModalDomain, ({ requestInfo }) => {
		const collectStatuses = [];

		if (requestInfo?.tab && !isEmpty(requestInfo?.tab)) {
			collectStatuses.push(requestInfo?.tab);
		}
		if (requestInfo?.status && !isEmpty(requestInfo?.status)) {
			collectStatuses.push(requestInfo?.status);
		}

		return collectStatuses;
	});

export const getIsButtonsDisabledSelector = () =>
	createSelector(
		carChangeModalDomain,
		({ isCarChanging, isRequestInfoLoading }) => isCarChanging || isRequestInfoLoading,
	);

export const getIsCarsLoadingSelector = () =>
	createSelector(carChangeModalDomain, ({ isCarsLoading }) => isCarsLoading);

export const getCarsOptionsSelector = () => createSelector(carChangeModalDomain, ({ cars }) => cars);

export const getTrailersOptionsSelector = () => createSelector(carChangeModalDomain, ({ trailers }) => trailers);

export const getDriversOptionsSelector = () => createSelector(carChangeModalDomain, ({ drivers }) => drivers);

export const getFormIsInvalidSelector = () => createSelector(isInvalid(FORM_NAME), isInvalid => isInvalid);

export const getAddCarFormIsInvalidSelector = () =>
	createSelector(isInvalid(ADD_CAR_FORM_NAME), isInvalid => isInvalid);

export const getAddTrailerFormIsInvalidSelector = () =>
	createSelector(isInvalid(ADD_TRAILER_FORM_NAME), isInvalid => isInvalid);

export const getAddCarFormInitialValuesSelector = () =>
	createSelector(carChangeModalDomain, () => {
		return {
			trailer: 'none',
			tonnage_id: 'none',
		};
	});

export const getAddCarForCreateValuesSelector = () =>
	createSelector(getFormValues(ADD_CAR_FORM_NAME), formValues => ({
		...formValues,
		trailer_id: formValues?.trailer === 'none' ? null : formValues?.trailer,
		tonnage_id: formValues?.tonnage_id === 'none' ? null : formValues?.tonnage_id,
	}));

export const getTrailersListSelector = () => createSelector(carChangeModalDomain, ({ trailersList }) => trailersList);

export const getIsTrailersListLoadingSelector = () =>
	createSelector(carChangeModalDomain, ({ isTrailersListLoading }) => isTrailersListLoading);

export const getAddTrailerFormInitialValuesSelector = () =>
	createSelector(carChangeModalDomain, () => {
		return {
			tonnage_id: 'none',
		};
	});

export const getAddTrailerForCreateValuesSelector = () =>
	createSelector(getFormValues(ADD_TRAILER_FORM_NAME), formValues => ({
		...formValues,
		tonnage_id: formValues.tonnage_id === 'none' ? null : formValues.tonnage_id,
	}));

export const getIsCarSavingSelector = () => createSelector(carChangeModalDomain, ({ isCarSaving }) => isCarSaving);

export const getIsCarChangingSelector = () =>
	createSelector(carChangeModalDomain, ({ isCarChanging }) => isCarChanging);

export const getIsTrailerSavingSelector = () =>
	createSelector(carChangeModalDomain, ({ isTrailerSaving }) => isTrailerSaving);

export const getTonnagesSelector = () => createSelector(carChangeModalDomain, ({ tonnages }) => tonnages);

export const getIsTonnagesLoadingSelector = () =>
	createSelector(carChangeModalDomain, ({ isTonnagesLoading }) => isTonnagesLoading);
